// src/pages/ChatPolicies.jsx
// Pantalla de gestión de permisos del chat (admin + leader). Compone:
//  - Botón "Refrescar catálogo" (Intranet, cache 4h)
//  - Card de Privilegios universales
//  - Lista de Cards de Protecciones (+ botón "Nueva")
//  - Tester par-a-par
//  - Tester en bloque
import React, { useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import PrivilegeCard      from '../components/chatPolicy/PrivilegeCard';
import ProtectionCard     from '../components/chatPolicy/ProtectionCard';
import PolicyTester       from '../components/chatPolicy/PolicyTester';
import BulkPolicyTester   from '../components/chatPolicy/BulkPolicyTester';
import {
    getCatalog, refreshCatalog, listProtections, createProtection,
} from '../services/chatPolicyApi';

function fmtDate(iso) {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function ChatPolicies() {
    const [catalog, setCatalog]   = useState({ puestos: [], unidades: [], sucursales: [], paises: [], meta: {} });
    const [protections, setProts] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [creating, setCreating] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        Promise.all([getCatalog(), listProtections()])
            .then(([cat, prot]) => {
                setCatalog(cat);
                setProts(prot);
            })
            .catch(() => toast.error('No se pudieron cargar las policies'))
            .finally(() => setLoading(false));
    }, []);

    const newProtection = async () => {
        setCreating(true);
        try {
            const p = await createProtection({
                description: 'Nueva protección',
                enabled: true,
                target: { puestos: [], unidades: [], sucursales: [], paises: [], users: [] },
                allowedSenders: [],
            });
            setProts(prev => [p, ...prev]);
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo crear');
        } finally { setCreating(false); }
    };

    const doRefresh = async () => {
        setRefreshing(true);
        try {
            const fresh = await refreshCatalog();
            setCatalog(fresh);
            toast.success(`Catálogo actualizado (fuente: ${fresh.meta?.source})`);
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo refrescar');
        } finally { setRefreshing(false); }
    };

    return (
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: 16 }}>
            <h4 style={{ fontWeight: 700, marginBottom: 4, color: 'var(--c-text-1)' }}>Permisos de chat</h4>
            <p style={{ fontSize: 13, color: 'var(--c-text-4)', marginBottom: 12 }}>
                Define qué puestos pueden iniciar chat con quién. Por defecto, dos personas pueden chatear si comparten al menos un departamento.
                Aquí puedes <strong>ampliar</strong> permisos (privilegios universales) o <strong>restringir</strong> (proteger destinatarios sensibles).
            </p>

            {/* Catálogo info + refresh */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: 'var(--c-card)', border: '1px solid var(--c-border)', borderRadius: 10,
                padding: '8px 12px', marginBottom: 18, fontSize: 12,
            }}>
                <div style={{ color: 'var(--c-text-4)' }}>
                    Catálogo: <strong>{catalog.puestos?.length || 0}</strong> puestos · <strong>{catalog.unidades?.length || 0}</strong> unidades · <strong>{catalog.sucursales?.length || 0}</strong> sucursales · <strong>{catalog.paises?.length || 0}</strong> países
                    {catalog.meta?.source && (
                        <span style={{ marginLeft: 10 }}>
                            (fuente: <strong>{catalog.meta.source}</strong>; actualizado {fmtDate(catalog.meta.fetchedAt)})
                        </span>
                    )}
                </div>
                <Button size="sm" variant="outline-secondary" onClick={doRefresh} disabled={refreshing}>
                    {refreshing ? 'Refrescando…' : 'Refrescar desde Intranet'}
                </Button>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 32 }}><Spinner animation="border" /></div>
            ) : (
                <>
                    <div style={{ marginBottom: 24 }}>
                        <PrivilegeCard catalog={catalog} />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <h5 style={{ fontWeight: 700, margin: 0 }}>Protecciones</h5>
                        <Button size="sm" variant="primary" onClick={newProtection} disabled={creating} style={{ borderRadius: 8 }}>
                            + Nueva protección
                        </Button>
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--c-text-4)', marginBottom: 12 }}>
                        Cada protección define un grupo de destinatarios sensibles y la lista de remitentes autorizados.
                        Si alguien matchea el destinatario pero no la lista, no podrá iniciarle chat aunque compartan departamento.
                    </p>

                    {protections.length === 0 ? (
                        <p style={{ fontStyle: 'italic', color: 'var(--c-text-4)' }}>No hay protecciones configuradas.</p>
                    ) : protections.map(p => (
                        <ProtectionCard
                            key={p._id}
                            protection={p}
                            catalog={catalog}
                            onUpdated={(u) => setProts(prev => prev.map(x => x._id === u._id ? u : x))}
                            onRemoved={(id) => setProts(prev => prev.filter(x => x._id !== id))}
                        />
                    ))}

                    <div style={{ marginTop: 24 }}>
                        <PolicyTester catalog={catalog} />
                    </div>

                    <div style={{ marginTop: 16 }}>
                        <BulkPolicyTester catalog={catalog} />
                    </div>
                </>
            )}
        </div>
    );
}
